import * as React from 'react';

import { DataTable } from '@tag/tag-components-react-v4';
import { BaseControlProps } from '../../models/BaseControlProps';
import { Message } from '../../models/Message';
import moment from 'moment';
import { IconButton } from '@mui/material';
import MessageDetail from '../MessageDetail/MessageDetail';
import ReadMoreIcon from '@mui/icons-material/ReadMore';
import { AttachFile, Description, PictureAsPdf } from '@mui/icons-material';
import ImageIcon from '@mui/icons-material/Image';



interface MessageDataTableProps extends BaseControlProps {
    loading: Boolean;
    userId: string;
    data: Message[];
    setData: (messages: Message[]) => void;
    messageCount?: number;
    setMessageCount?: (number: number) => void;
}


export default function MessageDataTable(props: MessageDataTableProps) {
    const maxLength = 150;
    const [showDetail, setShowDetail] = React.useState(false);
    const [messageDetail, setMessageDetail ] = React.useState<Message>();


    function renderViewButton({ row }: any) {
        return (
            <IconButton 
                aria-label='read-message'
                size='small'
                color='primary'
                onClick={() => {
                    setMessageDetail(row.original);
                    setShowDetail(true);
                }}
            >
                <ReadMoreIcon />
            </IconButton>
        );
    }

    function getAttachmentIcon(fileName: string) {
        const extension = fileName.split('.').pop()?.toLowerCase();
        switch (extension) {
            case 'png':
            case 'jpg':
            case 'jpeg':
            case 'gif':
                return <ImageIcon style={{ fontSize: '20px' }} />;
            case 'pdf':
                return <PictureAsPdf style={{ color:'red', fontSize: '20px' }} />;
            case 'doc':
            case 'docx':
                return <Description style={{ color: 'blue', fontSize: '20px' }} />;
            default:
                return <AttachFile style={{ fontSize: '20px' }} />;
        }
    }
    
    const columns = React.useMemo(
        () => [
        { 
            accessorKey: 'createdOn', 
            header: 'Date', 
            meta: { dataType: 'date' },
            cell: ({ row }: any) => {
                const date = row.createdOn;
                return date == '1900-01-01T00:00:00'
                    ? '-'
                    : moment(date).format('DD-MM-YY HH:mm');
            },
        },
        { 
            accessorKey: 'messageContent', 
            header: 'Message',
            cell: ({ row }: any) => {
                const content = row.original.messageContent
                    .replace(/&nbsp;/g, ' ') // Replace &nbsp; with regular space first
                    .replace(/<\/?[^>]+(>|$)/g, "") // Strip HTML tags
                    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
                    .trim() // Remove leading/trailing whitespace
                    .slice(0, 60); // Limit to 60 characters
                return content + (row.original.messageContent.length > 60 ? '...' : '');
            },
        },
        {
            header: 'Attachments',
            cell: ({row}: any) => {
                const attachments = row.original.attachments;

                return (
                    <div style={{ display: 'flex', gap: '8px' }}>
                    {attachments.map((attachment: any) => (
                        <div key={attachment.portalMessageAttachmentId}>
                            {getAttachmentIcon(attachment.attachmentName)}
                        </div>
                    ))}
                </div>
                )
            }
        },
        {
            accessorKey: 'view',
            header: () => <div style={{ textAlign: 'center' }}></div>,
            cell: renderViewButton,
            meta: {
                getCellProps: () => {
                    return { style: { textAlign: 'center' } };
                },
            },
        },
        ],
        [],
    );

  return (
    <>
        <DataTable 
            columns={columns} 
            data={props.data} 
            tableProps={{
                role: 'presentation'
            }} 
        />
        {messageDetail && (
            <MessageDetail 
                visible={showDetail}
                setVisible={setShowDetail}
                message={messageDetail}
                markAsRead={true}
                messageCount={props.messageCount}
                setMessageCount={props.setMessageCount}
            />
        )}
    </>
  );
}